import { axiosClient as axios } from './axiosClient';

import { api } from '@config/apiEndpoints';
import {
  failureResult,
  ResultViewModel,
  successResult,
} from '@models/common/ResultViewModel';
import { CustomerSelectOptionResultViewModel } from '@models/Customer';
import {
  ConvertToInvoiceResultViewModel,
  EstimateAndQuoteResultViewModel,
  EstimateAndQuoteTableResultViewModel,
  EstimateAndQuoteViewModel,
} from '@models/EstimateAndQuote';
import { EstimateAndQuoteDetailListResultViewModel } from '@models/EstimateAndQuoteDetail';
import { handleAxiosError } from '@utils/axiosErrorHandler';
import { getDataUrl } from '@utils/dataUrls';

export const EstimateAndQuoteService = {
  create: async function (
    estimateAndQuote: EstimateAndQuoteViewModel
  ): Promise<EstimateAndQuoteResultViewModel | ResultViewModel> {
    const createUrl = `${api.BASE_URL}${api.ESTIMATES_ENDPOINT}`;

    try {
      const response = await axios.post(createUrl, estimateAndQuote);
      return response.data as EstimateAndQuoteResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  getList: async function (
    page?: number,
    pageSize?: number,
    searchFields?: string,
    search?: string,
    filters?: string,
    sortBy?: string,
    sortDirection?: string
  ): Promise<EstimateAndQuoteTableResultViewModel | ResultViewModel> {
    let dataUrl = getDataUrl(
      api.BASE_URL,
      api.ESTIMATES_ENDPOINT,
      page,
      pageSize,
      searchFields,
      search,
      sortBy,
      sortDirection
    );

    if (filters) {
      dataUrl = dataUrl + filters;
    }

    try {
      const response = await axios.get(dataUrl);
      return response.data as EstimateAndQuoteTableResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  getById: async function (
    id: number
  ): Promise<EstimateAndQuoteResultViewModel | ResultViewModel> {
    const dataUrl = `${api.BASE_URL}${api.ESTIMATES_ENDPOINT}/${id}`;

    try {
      const response = await axios.get(dataUrl);
      return response.data as EstimateAndQuoteResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  update: async function (
    id: number,
    estimates: EstimateAndQuoteViewModel
  ): Promise<ResultViewModel> {
    const updateUrl = `${api.BASE_URL}${api.ESTIMATES_ENDPOINT}/${id}`;

    try {
      const response = await axios.put(updateUrl, estimates);
      if (response.status === 204) {
        return successResult;
      }

      return failureResult;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  delete: async function (id: number): Promise<ResultViewModel> {
    const deleteURL = `${api.BASE_URL}${api.ESTIMATES_ENDPOINT}/${id}`;

    try {
      const response = await axios.delete(deleteURL);
      if (response.status === 204) {
        return successResult;
      }

      return failureResult;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  approve: async function (id: number): Promise<ResultViewModel> {
    const approveUrl = `${api.BASE_URL}${api.ESTIMATES_ENDPOINT}/${id}/approve`;

    try {
      const response = await axios.post(approveUrl);
      if (response.status === 200) {
        return successResult;
      }

      return failureResult;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  sendEmail: async function (
    id: number,
    file?: File
  ): Promise<ResultViewModel> {
    const sendEmailUrl = `${api.BASE_URL}${api.ESTIMATES_ENDPOINT}/${id}/send-email`;
    const formData = new FormData();
    if (file) {
      formData.append('file', file);
    }

    try {
      const response = await axios.post(sendEmailUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      if (response.status === 200) {
        return successResult;
      }

      return failureResult;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  duplicate: async function (id: number): Promise<ResultViewModel> {
    const duplicateUrl = `${api.BASE_URL}${api.ESTIMATES_ENDPOINT}/${id}/duplicate`;

    try {
      const response = await axios.post(duplicateUrl);
      if (response.status === 200) {
        return successResult;
      }

      return failureResult;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  convertToInvoice: async function (
    id: number
  ): Promise<ConvertToInvoiceResultViewModel | ResultViewModel> {
    const convertToInvoiceUrl = `${api.BASE_URL}${api.ESTIMATES_ENDPOINT}/${id}/convert-to-invoice`;

    try {
      const response = await axios.post(convertToInvoiceUrl);
      return response.data as ConvertToInvoiceResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  getEstimateDetails: async function (
    id: number
  ): Promise<EstimateAndQuoteDetailListResultViewModel | ResultViewModel> {
    const dataUrl = `${api.BASE_URL}${api.ESTIMATES_ENDPOINT}/${id}/details`;

    try {
      const response = await axios.get(dataUrl);
      return response.data as EstimateAndQuoteDetailListResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  getCustomerFilterSelectOptions: async function (): Promise<
    CustomerSelectOptionResultViewModel | ResultViewModel
  > {
    const dataUrl = `${api.BASE_URL}${api.ESTIMATES_ENDPOINT}/customer-filter-select-options`;

    try {
      const response = await axios.get(dataUrl);
      return response.data as CustomerSelectOptionResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
};
